import React, { useState } from "react"
import { TextField } from "@mui/material"
import { useCombobox, UseComboboxStateChangeTypes } from "downshift"
import clsx from "clsx"
import { Trash } from "react-bootstrap-icons"
import { CollaboratorEntry } from "../../types/events"

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface Suggestion {
  band_id: number
  band_name: string
}

interface CollaboratorsFormProps {
  collaborators: CollaboratorEntry[]
  suggestions?: Suggestion[]
  postId?: number
  onAdd: (band: {
    id: number | null
    name: string
    cover_image_url: string | null
    email?: string
  }) => void
  onRemove: (collab: CollaboratorEntry) => void
}

const STATUS_LABELS: Record<string, string> = {
  pending: "Invited",
  accepted: "Accepted",
  declined: "Declined",
}

const STATUS_CLASSES: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  accepted: "bg-green-100 text-green-800",
  declined: "bg-red-100 text-red-700",
}

const STATUS_TEXT_CLASSES: Record<string, string> = {
  pending: "text-yellow-800",
  accepted: "text-green-800",
  declined: "text-red-700",
}

const CollaboratorsForm: React.FC<CollaboratorsFormProps> = ({
  collaborators,
  suggestions = [],
  postId,
  onAdd,
  onRemove,
}) => {
  const [searchResults, setSearchResults] = useState<any[]>([])
  const [inputValue, setInputValueState] = useState("")
  const [selectedBand, setSelectedBand] = useState<{
    id: number
    name: string
    cover_image_url: string | null
  } | null>(null)

  const isEmailInput = EMAIL_REGEX.test(inputValue.trim())

  const searchBands = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([])
      return
    }
    try {
      const response = await fetch(
        `/api/v1/search/bands/${encodeURIComponent(query)}?exclude_fans=true`,
      )
      const data = await response.json()
      setSearchResults(data.success && data.bands?.length > 0 ? data.bands : [])
    } catch {
      setSearchResults([])
    }
  }

  const selectTypes: UseComboboxStateChangeTypes[] = [
    useCombobox.stateChangeTypes.ItemClick,
    useCombobox.stateChangeTypes.InputKeyDownEnter,
  ]

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
    setInputValue,
  } = useCombobox({
    items: searchResults,
    onInputValueChange: ({ inputValue: val, type }) => {
      if (selectTypes.includes(type)) return
      if (val !== undefined) {
        setSelectedBand(null)
        setInputValueState(val)
        searchBands(val)
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        setSelectedBand({
          id: selectedItem.id,
          name: selectedItem.name,
          cover_image_url: selectedItem.cover_image_url ?? null,
        })
        setInputValueState(selectedItem.name)
        setSearchResults([])
        closeMenu()
      }
    },
    itemToString: (item) => (item ? item.name : ""),
  })

  const handleRemove = async (collab: CollaboratorEntry) => {
    if (collab.id && postId) {
      try {
        const csrf = (
          document.querySelector("meta[name=csrf-token]") as HTMLMetaElement
        )?.content
        await fetch(
          `/api/v1/posts/${postId}/post_collaborations/${collab.id}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": csrf ?? "",
              "Authorization": `Bearer ${localStorage.getItem("jwt_token") ?? ""}`,
            },
            credentials: "same-origin",
          },
        )
      } catch {
        // fall through — still remove from local state
      }
    }
    onRemove(collab)
  }

  const handleAdd = () => {
    if (!selectedBand) return
    const alreadyAdded = collaborators.some(
      (c) => c.band_id === selectedBand.id,
    )
    if (alreadyAdded) return
    onAdd(selectedBand)
    setSelectedBand(null)
    setInputValue("")
    setInputValueState("")
  }

  const handleEmailInvite = () => {
    const email = inputValue.trim()
    if (!EMAIL_REGEX.test(email)) return
    const alreadyAdded = collaborators.some(
      (c) => c.pending_band_email === email,
    )
    if (alreadyAdded) return
    onAdd({ id: null, name: email, cover_image_url: null, email })
    setInputValue("")
    setInputValueState("")
    closeMenu()
  }

  const showDropdown = isOpen && (searchResults.length > 0 || isEmailInput)

  const visible = collaborators.filter((c) => !c._destroy)

  return (
    <div className="my-4">
      <label className="block text-gray-900 text-xs uppercase font-bold">
        Collaborators
      </label>
      <p className="text-xs text-gray-500 mb-1">
        Invite other bands to co-edit this event. They'll receive an invite to
        accept.
      </p>

      {/* Search + invite button side by side */}
      <div className="flex items-start gap-2">
        <div className="relative flex-1">
          <TextField
            {...getInputProps({
              placeholder: "Search for collaborators or enter an email…",
              onFocus: () => {
                if (searchResults.length > 0) openMenu()
              },
            })}
            size="small"
            sx={{
              "width": "100%",
              "& .MuiInputBase-input": {
                color: selectedBand ? "#2f855a" : "#374151",
                fontWeight: selectedBand ? "bold" : "normal",
              },
            }}
          />

          <ul
            {...getMenuProps()}
            className={clsx(
              "absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto",
              { hidden: !showDropdown },
            )}
          >
            {isOpen &&
              searchResults.map((band, index) => (
                <li
                  key={band.id}
                  {...getItemProps({ item: band, index })}
                  className={clsx(
                    "px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center space-x-3",
                    highlightedIndex === index && "bg-blue-100 font-medium",
                    "hover:bg-gray-100",
                  )}
                >
                  <div className="flex-shrink-0 mr-2">
                    {band.cover_image_url ? (
                      <img
                        src={band.cover_image_url}
                        alt={band.name}
                        className="w-8 h-8 rounded-sm object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-sm bg-gray-200 flex items-center justify-center">
                        <span
                          className="text-gray-400 text-xs"
                          role="img"
                          aria-label="music note"
                        >
                          🎵
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm leading-tight">
                      {band.name}
                    </div>
                    {band.location?.displayName && (
                      <div className="text-xs text-gray-500 leading-tight">
                        {band.location.displayName}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            {isOpen && isEmailInput && (
              <li
                className="px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-blue-50 border-t border-gray-100"
                onMouseDown={(e) => {
                  e.preventDefault()
                  handleEmailInvite()
                }}
              >
                <span className="text-sm text-blue-700 font-medium">
                  Invite <span className="font-bold">{inputValue.trim()}</span>{" "}
                  by email
                </span>
              </li>
            )}
          </ul>
        </div>

        <button
          type="button"
          disabled={!selectedBand && !isEmailInput}
          onClick={selectedBand ? handleAdd : handleEmailInvite}
          className={clsx(
            "flex-shrink-0 border px-4 py-2 text-sm rounded-sm transition-colors bg-white focus:outline-none",
            !selectedBand && !isEmailInput
              ? "border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed"
              : "border-gray-500 text-gray-700 hover:bg-gray-50 cursor-pointer",
          )}
          style={{ height: 40 }}
        >
          Invite
        </button>
      </div>

      {/* Suggestions */}
      {(() => {
        const addedIds = new Set(
          collaborators.filter((c) => !c._destroy).map((c) => c.band_id),
        )
        const available = suggestions.filter((s) => !addedIds.has(s.band_id))
        if (available.length === 0) return null
        return (
          <div className="mt-1">
            <p className="text-xs text-gray-400">Suggestions</p>
            <ul className="space-y-1">
              {available.map((s) => (
                <li key={s.band_id}>
                  <button
                    type="button"
                    onClick={() =>
                      onAdd({
                        id: s.band_id,
                        name: s.band_name,
                        cover_image_url: null,
                      })
                    }
                    className="inline-flex items-start gap-1 text-left text-xs text-gray-500 hover:text-gray-800 focus:outline-none"
                  >
                    <span className="flex-shrink-0">+</span>
                    <span>{s.band_name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )
      })()}

      {/* Collaborator list */}
      {visible.length > 0 && (
        <ul className="mt-3 space-y-1">
          {visible.map((collab) => (
            <li
              key={collab.band_id ?? collab.pending_band_email}
              className="flex items-center justify-between"
            >
              <span
                className={clsx(
                  "text-sm truncate",
                  STATUS_TEXT_CLASSES[collab.status] ?? "text-gray-700",
                )}
              >
                {collab.band_name}
                {collab.pending_band_email && !collab.band_id && (
                  <span className="text-xs text-gray-400 ml-1">
                    (email invite)
                  </span>
                )}
              </span>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                {collab.id && (
                  <span
                    className={clsx(
                      "text-xs px-1.5 py-0.5 rounded-full font-medium",
                      STATUS_CLASSES[collab.status] ??
                        "bg-gray-100 text-gray-700",
                    )}
                  >
                    {STATUS_LABELS[collab.status] ?? collab.status}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleRemove(collab)}
                  className="p-1 rounded transition-colors focus:outline-none text-red-600 hover:text-red-800 hover:bg-red-50"
                  aria-label={`Remove ${collab.band_name}`}
                >
                  <Trash className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default CollaboratorsForm
